import { useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

/**
 * Reusable "Show more / Show less" grid with motion-safe mounting.
 *
 * WHY THIS EXISTS — the regression we are guarding against:
 * Framer Motion's `whileInView` with `viewport={{ once: true }}` (used by
 * our `ScrollReveal` / stagger containers) finishes its variant orchestration
 * after the first reveal. Children that mount LATER (e.g. when a user clicks
 * "Show more") inherit the parent's stale `hidden` variant and stay invisible
 * even though they are in the DOM. The slicing math looks correct, but the
 * user sees nothing new on screen — a confusing, hard-to-debug class of bug.
 *
 * This component sidesteps the problem entirely by giving each item its OWN
 * `initial` / `animate` props instead of relying on inherited variants. That
 * way, items revealed by the toggle always animate from `hidden` to `visible`
 * on mount, regardless of the parent stagger's lifecycle state.
 *
 * Usage:
 *   <ExpandableGrid
 *     items={certs}
 *     initialVisible={3}
 *     gridClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
 *     renderItem={(cert) => <CertCard cert={cert} />}
 *     getItemKey={(cert) => cert.id}
 *   />
 */

export interface ExpandableGridProps<T> {
  items: T[];
  /** Number of items shown before the "Show more" toggle appears. */
  initialVisible: number;
  renderItem: (item: T, index: number) => ReactNode;
  getItemKey: (item: T, index: number) => string;
  /** Tailwind classes for the grid container. */
  gridClassName?: string;
  /** Label for the "show more" button. Receives the hidden count. */
  showMoreLabel?: (hiddenCount: number) => string;
  showLessLabel?: string;
  /** Optional extra content rendered inside the grid (e.g. empty state). */
  trailingSlot?: ReactNode;
  className?: string;
  /**
   * Optional per-item wrapper classes (applied to the motion.div).
   * Useful for responsive visibility tweaks like hiding the 3rd card on
   * tablet when the row would otherwise wrap awkwardly.
   */
  getItemWrapperClassName?: (item: T, index: number, showAll: boolean) => string | undefined;
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

const expandedTransition = {
  duration: 0.4,
  ease: [0.25, 0.1, 0.25, 1] as const,
};

export function ExpandableGrid<T>({
  items,
  initialVisible,
  renderItem,
  getItemKey,
  gridClassName = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6",
  showMoreLabel = (n) => `Show more (${n})`,
  showLessLabel = "Show less",
  trailingSlot,
  className,
  getItemWrapperClassName,
}: ExpandableGridProps<T>) {
  const [showAll, setShowAll] = useState(false);

  const visible = showAll ? items : items.slice(0, initialVisible);
  const hasMore = items.length > initialVisible;
  const hiddenCount = Math.max(0, items.length - initialVisible);

  return (
    <div className={className}>
      <div className={gridClassName}>
        {visible.map((item, index) => {
          // Items beyond the initial slice mount AFTER the parent stagger's
          // whileInView animation has completed. Inheriting variants would
          // leave them stuck at opacity:0 — animate them independently instead.
          const isExpanded = index >= initialVisible;
          const wrapperClass = getItemWrapperClassName?.(item, index, showAll);
          return (
            <motion.div
              key={getItemKey(item, index)}
              className={wrapperClass}
              // Each item animates on its own (never inherits parent variants),
              // so cards that mount after the parent reveal - e.g. when live
              // data replaces a placeholder list - never get stuck invisible.
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={
                isExpanded
                  ? expandedTransition
                  : { ...itemVariants.visible.transition, delay: Math.min(index, 8) * 0.08 }
              }
            >
              {renderItem(item, index)}
            </motion.div>
          );
        })}
        {trailingSlot}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-8">
          <Button variant="outline" onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? showLessLabel : showMoreLabel(hiddenCount)}
          </Button>
        </div>
      )}
    </div>
  );
}

export default ExpandableGrid;